import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { IdentificationCard, DownloadSimple, ArrowsClockwise, ShieldCheck, Buildings } from "@phosphor-icons/react";
import UserAvatar from "@/components/UserAvatar";
import { getAvatarUrl } from "@/lib/avatar";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Personal "wallet entry card" with a server-signed QR (24h TTL). Designed to
 * be a single screen so the user can show it at the club's front desk and an
 * authorized staff member scans it via POST /api/wallet-card/verify.
 */
export default function MyWalletCard() {
  const { user } = useAuth();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const canvasWrapperRef = useRef(null);

  const fetchCard = async () => {
    setLoading(true);
    try {
      const res = await api.get("/me/wallet-card");
      setData(res.data);
    } catch (e) {
      toast.error("No se pudo cargar la tarjeta");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchCard(); }, []);

  const downloadPng = () => {
    const canvas = canvasWrapperRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `dyd-wallet-${(data?.name || "card").replace(/\s+/g, "-").toLowerCase()}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR descargado");
  };

  const memberSince = data?.member_since ? new Date(data.member_since).getFullYear() : "—";
  const initials = (data?.name || "").split(" ").filter(Boolean).slice(0, 2).map(s => s[0]).join("").toUpperCase();

  return (
    <div className="space-y-6" data-testid="wallet-card-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2">
          <IdentificationCard size={14} weight="fill" /> {t("wallet.titulo") || "Mi tarjeta de ingreso"}
        </p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">
          {t("wallet.subtitulo") || "Wallet DyD"}
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          {t("wallet.desc") || "Tu credencial digital con QR personal. Preséntala en la entrada del club."}
        </p>
      </div>

      {loading && <p className="text-center text-gray-500 py-12">{t("common.cargando") || "Cargando..."}</p>}

      {!loading && data && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* The card itself — printable / shareable layout */}
          <div
            data-testid="wallet-card-printable"
            className="relative rounded-2xl overflow-hidden p-6 shadow-2xl shadow-[#CCFF00]/10"
            style={{
              background: "linear-gradient(135deg, #0A0A0A 0%, #141414 60%, #1A2410 100%)",
              border: "1px solid rgba(204,255,0,0.35)",
              minHeight: 380,
            }}
          >
            {/* Decorative grain */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{
              backgroundImage: "radial-gradient(circle at 20% 20%, #CCFF00 0%, transparent 60%), radial-gradient(circle at 80% 80%, #FFFFFF 0%, transparent 50%)",
            }} />

            <div className="relative flex items-start justify-between gap-3 mb-6">
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold">Disfruta y Diviértete</p>
                <p className="text-[10px] tracking-[0.15em] uppercase text-gray-400 mt-0.5">Wallet Card · v1</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#CCFF00]/15 border border-[#CCFF00]/40 flex items-center justify-center">
                <ShieldCheck size={22} weight="fill" className="text-[#CCFF00]" />
              </div>
            </div>

            <div className="relative flex items-center gap-4 mb-6">
              <UserAvatar
                src={data.photo || getAvatarUrl({ name: data.name, id: data.user_id })}
                fallback={initials}
                className="w-16 h-16 rounded-full border-2 border-[#CCFF00]/40"
              />
              <div className="min-w-0 flex-1">
                <p className="text-white font-['Barlow_Condensed'] text-2xl font-black uppercase truncate" data-testid="wallet-card-name">{data.name}</p>
                <p className="text-[11px] tracking-wider uppercase text-[#CCFF00] mt-1" data-testid="wallet-card-role">
                  {t(`roles.${data.role}`) || data.role}
                  {data.category && <span className="text-gray-400"> · Cat {data.category}</span>}
                </p>
                {data.club_id && (
                  <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1"><Buildings size={11} /> {data.club_id.slice(0, 8)}</p>
                )}
              </div>
            </div>

            <div className="relative flex items-end justify-between gap-3">
              <div className="text-[10px] tracking-[0.15em] uppercase text-gray-400">
                <p>ID · <span className="text-gray-200 font-mono">{(data.user_id || "").slice(0, 8)}</span></p>
                <p className="mt-0.5">Miembro desde · <span className="text-gray-200">{memberSince}</span></p>
              </div>
              <div ref={canvasWrapperRef} className="bg-white p-2 rounded shadow-md" data-testid="wallet-card-qr">
                <QRCodeCanvas
                  value={data.qr_token}
                  size={120}
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                  level="M"
                  includeMargin={false}
                />
              </div>
            </div>
          </div>

          {/* Side panel with actions and info */}
          <div className="space-y-4">
            <Card className="bg-black/40 border-white/10">
              <CardContent className="p-5">
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#CCFF00] mb-3">Cómo usarla</p>
                <ol className="space-y-2 text-sm text-gray-200">
                  <li className="flex gap-2"><span className="text-[#CCFF00] font-bold">1.</span> Muestra esta pantalla al staff de entrada de tu club.</li>
                  <li className="flex gap-2"><span className="text-[#CCFF00] font-bold">2.</span> Ellos escanean el QR con la app DyD para confirmar tu identidad.</li>
                  <li className="flex gap-2"><span className="text-[#CCFF00] font-bold">3.</span> El QR se renueva automáticamente cada 24h por seguridad.</li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-black/40 border-white/10">
              <CardContent className="p-5">
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#CCFF00] mb-3">Acciones</p>
                <div className="flex flex-col gap-2">
                  <Button onClick={downloadPng} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="wallet-download-btn">
                    <DownloadSimple size={14} weight="bold" className="mr-2" /> Descargar QR (PNG)
                  </Button>
                  <Button onClick={fetchCard} variant="outline" className="border-white/20 text-gray-300 hover:text-[#CCFF00] hover:border-[#CCFF00]/40 font-bold uppercase tracking-wide text-xs" data-testid="wallet-refresh-btn">
                    <ArrowsClockwise size={14} className="mr-2" /> Renovar QR
                  </Button>
                </div>
                <p className="text-[10px] text-gray-500 mt-3 leading-relaxed">
                  El token QR caduca a las 24 horas. Renueva si caducó o si sospechas que alguien lo copió.
                </p>
              </CardContent>
            </Card>

            {(user?.role === "admin" || user?.role === "directivo" || user?.role === "profesor") && (
              <Card className="bg-[#CCFF00]/5 border-[#CCFF00]/30">
                <CardContent className="p-5">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#CCFF00] mb-2">Staff</p>
                  <p className="text-sm text-gray-200 mb-3">¿Vas a escanear el QR de un jugador?</p>
                  <Button asChild className="w-full bg-[#CCFF00]/15 text-[#CCFF00] hover:bg-[#CCFF00]/25 font-bold uppercase tracking-wide text-xs" data-testid="wallet-scan-link">
                    <a href="/dashboard/wallet-scan">Abrir escáner</a>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
